import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { graphql } from '@/gql';
import type { FieldKind, UpdateProfileInput } from '@/gql/graphql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { refuseAll, screenAll, useEmailGuard } from '@/lib/useEmailCheck';
import { useSession } from '@/features/session/useSession';

const CompletenessDoc = graphql(/* GraphQL */ `
  query OnboardingCompleteness {
    me {
      completeness {
        status
        missingFields {
          name
          label
          kind
          required
          options
        }
      }
    }
  }
`);

const UpdateMyProfileDoc = graphql(/* GraphQL */ `
  mutation OnboardingUpdateProfile($input: UpdateProfileInput!) {
    updateMyProfile(input: $input) {
      id
      firstName
      lastName
    }
  }
`);

function inputType(kind: FieldKind): string {
  switch (kind) {
    case 'DATE':
      return 'date';
    case 'PHONE':
      return 'tel';
    default:
      return 'text';
  }
}

function profileInputFromValues(values: Record<string, string>): UpdateProfileInput {
  const str = (key: string) => {
    const v = values[key]?.trim();
    return v ? v : undefined;
  };
  return {
    firstName: str('firstName'),
    lastName: str('lastName'),
    email: str('email'),
    dateOfBirth: str('dateOfBirth'),
    phone: str('phone'),
    address: str('address'),
    postalCode: str('postalCode'),
    city: str('city'),
    country: str('country'),
    gender: str('gender'),
    pronouns: str('pronouns'),
    iban: str('iban'),
    chamberOfCommerce: str('chamberOfCommerce'),
    vatNumber: str('vatNumber'),
    invoiceEmail: str('invoiceEmail'),
  };
}

/**
 * Dynamic required-info gate (1.3). Renders **required** missing fields from
 * the completeness payload rather than a hardcoded last-name/DOB screen, and
 * rather than every optional attribute listed for the Home nudge.
 *
 * Every field except `passkey` is written through `updateMyProfile`. Passkey
 * registration is a ceremony, not a profile column — a registration link from
 * an administrator is required.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { me, refresh } = useSession();
  const { data, error, loading, refetch } = useQuery(CompletenessDoc, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const [save, { loading: saving }] = useMutation(UpdateMyProfileDoc);
  const completeness = data?.me.completeness;
  const missing = (completeness?.missingFields ?? []).filter(
    (field) => field.required,
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  if (completeness && !seeded) {
    setSeeded(true);
    const next: Record<string, string> = {};
    if (me?.firstName) next.firstName = me.firstName;
    if (me?.lastName) next.lastName = me.lastName;
    setValues(next);
  }

  const writable = useMemo(
    () =>
      new Set([
        'firstName',
        'lastName',
        'email',
        'dateOfBirth',
        'phone',
        'address',
        'postalCode',
        'city',
        'country',
        'gender',
        'pronouns',
        'iban',
        'chamberOfCommerce',
        'vatNumber',
        'invoiceEmail',
      ]),
    [],
  );

  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();
  const invoiceEmailGuard = useEmailGuard();
  const guards = { email: emailGuard, invoiceEmail: invoiceEmailGuard };

  const submit = async (next: Record<string, string>) => {
    setFormError(null);
    setChecking(true);
    try {
      const screened = await screenAll([
        [emailGuard, next.email?.trim() || null],
        [invoiceEmailGuard, next.invoiceEmail?.trim() || null],
      ]);
      if (!screened) return;
      await save({ variables: { input: profileInputFromValues(next) } });
      await refresh();
      const again = await refetch();
      if (again.data?.me.completeness.status === 'COMPLETE') {
        navigate('/', { replace: true });
      }
    } catch (err) {
      refuseAll(err, [
        [emailGuard, next.email?.trim() ?? ''],
        [invoiceEmailGuard, next.invoiceEmail?.trim() ?? ''],
      ]);
      setFormError(graphqlErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submit(values);
  };

  /** Use / Keep on a prompt: set the field to the picked address and save at once. */
  const resubmit = (field: string, address: string) => {
    const next = { ...values, [field]: address };
    setValues(next);
    void submit(next);
  };

  if (loading && !completeness && !error) {
    return <p className="text-sm text-gray-500">Checking your profile…</p>;
  }

  if (error && !completeness) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Complete your profile
        </h1>
        <QueryErrorNotice
          error={error}
          title="Profile completeness is not available yet"
        />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          The API this session is talking to does not expose staff completeness.
          You can still update your details on{' '}
          <button
            type="button"
            className="text-brand-600 underline"
            onClick={() => navigate('/profile')}
          >
            Profile
          </button>
          .
        </p>
      </div>
    );
  }

  if (completeness?.status === 'COMPLETE') {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          You&apos;re all set
        </h1>
        <p className="text-sm text-gray-500">Your profile is complete.</p>
        <Button onClick={() => navigate('/')}>Continue</Button>
      </div>
    );
  }

  return (
    <FormLayout
      title="Complete your profile"
      description="A few details are still required before you can continue."
      onSubmit={handleSubmit}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" loading={saving || checking}>
            Save and continue
          </Button>
          {formError ? <span className="text-sm text-red-600">{formError}</span> : null}
        </div>
      }
    >
      <FormSection title="Required information">
        {missing.length === 0 ? (
          <p className="text-sm text-gray-500">No fields to fill in.</p>
        ) : (
          missing.map((field) => {
            const canWrite = writable.has(field.name);
            return (
              <FormField
                key={field.name}
                label={field.label}
                required={field.required}
                description={
                  canWrite
                    ? undefined
                    : field.name === 'passkey'
                      ? 'Register a passkey from a registration link issued by an administrator.'
                      : undefined
                }
              >
                {field.kind === 'SELECT' && field.options ? (
                  <Select
                    value={values[field.name] ?? ''}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    disabled={!canWrite}
                    options={[
                      { value: '', label: 'Select…' },
                      ...field.options.map((o) => ({ value: o, label: o })),
                    ]}
                  />
                ) : (
                  <Input
                    type={inputType(field.kind)}
                    value={values[field.name] ?? ''}
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, [field.name]: e.target.value }));
                      if (field.name === 'email') emailGuard.changed();
                      if (field.name === 'invoiceEmail') invoiceEmailGuard.changed();
                    }}
                    required={field.required && canWrite}
                    disabled={!canWrite}
                  />
                )}
                {field.name === 'email' || field.name === 'invoiceEmail' ? (
                  <GuardSuggestion
                    guard={guards[field.name]}
                    idPrefix={`admin.onboarding.${field.name}`}
                    disabled={saving || checking}
                    onAnswer={(address) => resubmit(field.name, address)}
                  />
                ) : null}
              </FormField>
            );
          })
        )}
      </FormSection>
    </FormLayout>
  );
}
